import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { format, startOfDay } from 'date-fns';
import { cpApi } from '../api/api';
import type { VndCounterpartyRequest, Counterparty } from '../types';
import { useVndRequestsFilters } from '../context/VndRequestsFiltersContext';
import VndPageLoading from '../components/VndPageLoading';
import ConfirmApproveWidget from '../components/ConfirmApproveWidget';
import ConfirmCancelWidget from '../components/ConfirmCancelWidget';
import HoverTooltip from '../components/ui/HoverTooltip';
import { formatVndInt } from '../utils/formatNumber';
import { getAutoRefreshInterval, isAutoRefreshEnabled } from '../utils/preferences';
import { intlLocaleFor } from '../i18n/config';

const PAGE_SIZE = 12;

const statusClass: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  work: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  pending_canceled: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const statusCardToneClass: Record<string, string> = {
  open: 'border-blue-200/70 bg-blue-50/30 dark:border-blue-900/50 dark:bg-blue-950/15',
  work: 'border-amber-200/70 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/15',
  in_progress: 'border-amber-200/70 bg-amber-50/30 dark:border-amber-900/50 dark:bg-amber-950/15',
  closed: 'border-emerald-200/70 bg-emerald-50/30 dark:border-emerald-900/50 dark:bg-emerald-950/15',
  canceled: 'border-rose-200/70 bg-rose-50/30 dark:border-rose-900/50 dark:bg-rose-950/15',
  pending_canceled: 'border-purple-200/70 bg-purple-50/30 dark:border-purple-900/50 dark:bg-purple-950/15',
};

const TERMINAL_STATUSES = new Set(['closed', 'canceled', 'pending_canceled']);

const STATUS_I18N_KEYS: Record<
  string,
  'status_open' | 'status_work' | 'status_in_progress' | 'status_closed' | 'status_canceled' | 'status_pending_canceled'
> = {
  open: 'status_open',
  work: 'status_work',
  in_progress: 'status_in_progress',
  closed: 'status_closed',
  canceled: 'status_canceled',
  pending_canceled: 'status_pending_canceled',
};

function requestStatusLabel(t: TFunction, status: string): string {
  const k = STATUS_I18N_KEYS[status];
  return k ? t(`requests.${k}`) : status;
}

function formatCreatedDateTime(date: string, locale: string, na: string): string {
  if (!date) return na;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return na;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function workDurationMs(r: VndCounterpartyRequest): number | null {
  const start = new Date(r.created_at).getTime();
  if (Number.isNaN(start)) return null;
  let end: number | null = null;
  if (TERMINAL_STATUSES.has(r.status)) {
    if (r.work_finished_at) {
      const x = new Date(r.work_finished_at).getTime();
      if (!Number.isNaN(x)) end = x;
    }
    if (end == null && r.updated_at) {
      const x = new Date(r.updated_at).getTime();
      if (!Number.isNaN(x)) end = x;
    }
    if (end == null) return null;
  } else {
    end = Date.now();
  }
  if (Number.isNaN(end) || end < start) return null;
  return end - start;
}

function formatWorkDuration(ms: number, t: TFunction): string {
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return t('requests.durationLessThanMin');
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(t('requests.durationDay', { n: days }));
  if (hours > 0) parts.push(t('requests.durationHour', { n: hours }));
  if (mins > 0 || parts.length === 0) parts.push(t('requests.durationMin', { n: mins }));
  return parts.join(' ');
}

export default function VndRequestsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const intlLocale = intlLocaleFor(i18n.language);
  const { statusFilter, dateRange, searchQuery, profileId } = useVndRequestsFilters();

  const [rows, setRows] = useState<VndCounterpartyRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusyId, setActionBusyId] = useState<number | null>(null);
  const [approveConfirmRequest, setApproveConfirmRequest] = useState<VndCounterpartyRequest | null>(null);
  const [cancelConfirmRequest, setCancelConfirmRequest] = useState<VndCounterpartyRequest | null>(null);
  const [confirmBusyId, setConfirmBusyId] = useState<number | null>(null);
  const [approveConfirmBusy, setApproveConfirmBusy] = useState(false);
  const [cancelConfirmBusy, setCancelConfirmBusy] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => isAutoRefreshEnabled());
  const [autoRefreshIntervalSec, setAutoRefreshIntervalSec] = useState(() => getAutoRefreshInterval());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lockedRequestIds, setLockedRequestIds] = useState<Set<number>>(new Set());
  const [cpProfile, setCpProfile] = useState<Counterparty | null>(null);

  const setHeaderUpdatingState = useCallback((value: boolean) => {
    const win = window as unknown as { setAirexTableUpdating?: (v: boolean) => void };
    win.setAirexTableUpdating?.(value);
    window.dispatchEvent(new CustomEvent('vndRequestsUpdating', { detail: { updating: value } }));
  }, []);

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const tm = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(tm);
  }, [searchQuery]);

  const dateFrom = useMemo(
    () => (dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd') : ''),
    [dateRange.from]
  );
  const dateTo = useMemo(
    () => (dateRange.to ? format(startOfDay(dateRange.to), 'yyyy-MM-dd') : ''),
    [dateRange.to]
  );

  useLayoutEffect(() => {
    setPage(1);
  }, [statusFilter, dateFrom, dateTo, debouncedSearch, profileId]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = Boolean(opts?.silent);
      const startedAt = silent ? Date.now() : 0;
      if (!silent) {
        setLoading(true);
      } else {
        setHeaderUpdatingState(true);
      }
      setError(null);
      try {
        const res = await cpApi.getVndRequests({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          search: debouncedSearch.trim() || undefined,
          profile_id: profileId !== 'all' ? Number(profileId) : undefined,
          page,
          limit: PAGE_SIZE,
        });
        setRows(res.items);
        setTotalPages(Math.max(1, res.total_pages));
        window.dispatchEvent(new CustomEvent('vndInWorkCountRefresh'));
      } catch {
        setError(t('requests.loadError'));
        if (!silent) {
          setRows([]);
          setTotalPages(1);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          const elapsed = Date.now() - startedAt;
          const minVisibleMs = 500;
          const finalize = () => setHeaderUpdatingState(false);
          if (elapsed < minVisibleMs) {
            window.setTimeout(finalize, minVisibleMs - elapsed);
          } else {
            finalize();
          }
        }
      }
    },
    [statusFilter, dateFrom, dateTo, debouncedSearch, profileId, page, setHeaderUpdatingState, t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void cpApi
      .getProfile()
      .then((p) => {
        if (!cancelled) setCpProfile(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onRefresh = () => void load({ silent: true });
    window.addEventListener('refreshRequests', onRefresh);
    return () => window.removeEventListener('refreshRequests', onRefresh);
  }, [load]);

  useEffect(() => {
    const onAutoRefreshChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled?: boolean; interval?: number }>).detail;
      if (typeof detail?.enabled === 'boolean') {
        setAutoRefreshEnabled(detail.enabled);
      }
      if (typeof detail?.interval === 'number' && Number.isFinite(detail.interval)) {
        setAutoRefreshIntervalSec(Math.max(60, Math.min(300, Math.round(detail.interval))));
      }
    };
    window.addEventListener('autoRefreshChanged', onAutoRefreshChanged);
    return () => window.removeEventListener('autoRefreshChanged', onAutoRefreshChanged);
  }, []);

  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const id = window.setInterval(() => {
      void load({ silent: true });
    }, autoRefreshIntervalSec * 1000);
    return () => window.clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshIntervalSec, load]);

  useEffect(() => {
    if (loading) return;
    window.dispatchEvent(
      new CustomEvent('paginationUpdate', {
        detail: { currentPage: page, totalPages, showPagination: true },
      })
    );
  }, [page, totalPages, loading]);

  useEffect(() => {
    const onPageChange = (e: Event) => {
      const p = (e as CustomEvent<{ page: number }>).detail?.page;
      if (typeof p === 'number' && p >= 1) setPage(p);
    };
    window.addEventListener('pageChange', onPageChange);
    return () => window.removeEventListener('pageChange', onPageChange);
  }, []);

  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent('paginationUpdate', {
          detail: { currentPage: 1, totalPages: 1, showPagination: false },
        })
      );
    };
  }, []);

  const onApprove = useCallback(
    async (id: number) => {
      if (lockedRequestIds.has(id)) return;
      const prevStatus = rows.find((x) => x.id === id)?.status ?? '';
      setLockedRequestIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setActionBusyId(id);
      setError(null);
      try {
        await cpApi.approveVndRequest(id);
        // Подтверждение может применяться асинхронно через outbox; дожидаемся смены статуса.
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await load({ silent: true });
          const latest = await cpApi.getVndRequests({
            search: String(id),
            date_from: '1970-01-01',
            date_to: '2099-12-31',
            page: 1,
            limit: 50,
          });
          const current = latest.items.find((x) => x.id === id);
          const currentStatus = current?.status ?? '';
          if (currentStatus && (currentStatus !== prevStatus || TERMINAL_STATUSES.has(currentStatus))) {
            break;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 800));
        }
        await load({ silent: true });
      } catch {
        setError(t('requests.approveError', { id }));
      } finally {
        setActionBusyId(null);
        setLockedRequestIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [load, lockedRequestIds, rows, t]
  );

  const onCancel = useCallback(
    async (id: number, comment?: string) => {
      if (lockedRequestIds.has(id)) return;
      const prevStatus = rows.find((x) => x.id === id)?.status ?? '';
      setLockedRequestIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setActionBusyId(id);
      setError(null);
      try {
        await cpApi.cancelVndRequest(id, (comment ?? '').trim());
        // Отмена также может прийти не мгновенно — polling по конкретной заявке.
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await load({ silent: true });
          const latest = await cpApi.getVndRequests({
            search: String(id),
            date_from: '1970-01-01',
            date_to: '2099-12-31',
            page: 1,
            limit: 50,
          });
          const current = latest.items.find((x) => x.id === id);
          const currentStatus = current?.status ?? '';
          if (currentStatus && (currentStatus !== prevStatus || TERMINAL_STATUSES.has(currentStatus))) {
            break;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 800));
        }
        await load({ silent: true });
      } catch {
        setError(t('requests.cancelError', { id }));
      } finally {
        setActionBusyId(null);
        setLockedRequestIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [load, lockedRequestIds, rows, t]
  );

  const copyText = useCallback(
    async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        setError(t('requests.copyError'));
      }
    },
    [t]
  );

  const openApproveConfirm = useCallback((request: VndCounterpartyRequest) => {
    if (lockedRequestIds.has(request.id)) return;
    setConfirmBusyId(request.id);
    setApproveConfirmRequest(request);
  }, [lockedRequestIds]);

  const openCancelConfirm = useCallback((request: VndCounterpartyRequest) => {
    if (lockedRequestIds.has(request.id)) return;
    setConfirmBusyId(request.id);
    setCancelConfirmRequest(request);
  }, [lockedRequestIds]);

  const onApproveConfirmed = useCallback(async () => {
    if (!approveConfirmRequest) return;
    setApproveConfirmBusy(true);
    try {
      await onApprove(approveConfirmRequest.id);
      setApproveConfirmRequest(null);
      setConfirmBusyId(null);
    } finally {
      setApproveConfirmBusy(false);
    }
  }, [approveConfirmRequest, onApprove]);

  const onCancelConfirmed = useCallback(async () => {
    if (!cancelConfirmRequest) return;
    setCancelConfirmBusy(true);
    try {
      await onCancel(cancelConfirmRequest.id);
      setCancelConfirmRequest(null);
      setConfirmBusyId(null);
    } finally {
      setCancelConfirmBusy(false);
    }
  }, [cancelConfirmRequest, onCancel]);

  const na = t('common.notAvailable');

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4">
      {error && <div className="text-sm text-red-500">{error}</div>}

      <div className="rounded-xl border border-slate-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800/90 shadow-sm overflow-hidden">
        {loading ? (
          <VndPageLoading fillTable />
        ) : rows.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500 dark:text-gray-400">{t('requests.empty')}</div>
        ) : (
          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {rows.map((r) => {
                const label = requestStatusLabel(t, r.status);
                const statusCss = statusClass[r.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
                const cardToneCss =
                  statusCardToneClass[r.status] ||
                  'border-red-200/60 bg-white/95 dark:border-gray-700/70 dark:bg-gray-900/60';
                const manualIntake = Boolean(cpProfile?.manual_intake_enabled);
                const canDecide =
                  manualIntake &&
                  (r.status === 'open' ||
                  r.status === 'work' ||
                  r.status === 'in_progress' ||
                  r.status === 'pending_canceled');
                const qrName = (r.vnd_qr_profile_name || r.qr_profile_name || '').trim();
                const qrId = r.vnd_qr_profile_id ?? r.qr_profile_id;
                const qrProfileView =
                  qrName || qrId != null
                    ? `${qrName || t('requests.qrFallback')}${qrId != null ? ` · #${qrId}` : ''}`
                    : na;
                const vndVal = r.vnd_amount_calculated;
                const rawTeamUsdt = r.usdt_amount_client;
                const fixedCounterpartyRate =
                  r.vnd_tier_vnd_per_usdt != null && r.vnd_tier_vnd_per_usdt > 0
                    ? r.vnd_tier_vnd_per_usdt
                    : r.fixed_vnd_per_usdt != null && r.fixed_vnd_per_usdt > 0
                      ? r.fixed_vnd_per_usdt
                      : vndVal != null && vndVal > 0 && rawTeamUsdt != null && rawTeamUsdt > 0
                        ? vndVal / rawTeamUsdt
                        : null;
                const teamUsdt =
                  rawTeamUsdt != null && rawTeamUsdt > 0
                    ? rawTeamUsdt
                    : vndVal != null && vndVal > 0 && fixedCounterpartyRate != null && fixedCounterpartyRate > 0
                      ? vndVal / fixedCounterpartyRate
                      : null;
                const workMs = workDurationMs(r);
                const usdtNumber =
                  teamUsdt != null && teamUsdt > 0
                    ? Number(teamUsdt).toLocaleString(intlLocale, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : na;
                const usdtFullTooltip =
                  teamUsdt != null && teamUsdt > 0 ? `${teamUsdt} USDT` : na;
                const fixedRateNumber =
                  fixedCounterpartyRate != null && fixedCounterpartyRate > 0
                    ? Number(fixedCounterpartyRate).toLocaleString(intlLocale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })
                    : na;
                const durationText = workMs != null ? formatWorkDuration(workMs, t) : '';
                const durationLabel = TERMINAL_STATUSES.has(r.status)
                  ? t('requests.workDurationDone', { duration: durationText })
                  : t('requests.workDuration', { duration: durationText });
                return (
                  <article
                    key={r.id}
                    className={`rounded-xl border p-4 shadow-sm ${cardToneCss}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            void copyText(String(r.id));
                          }}
                          className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-base font-semibold text-blue-700 transition hover:scale-[1.03] hover:border-blue-300 dark:border-blue-800/70 dark:bg-blue-950/30 dark:text-blue-300"
                          title={t('requests.copyIdTitle')}
                        >
                          {r.id}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!r.order_id) return;
                            void copyText(r.order_id);
                          }}
                          className="rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-600 transition hover:scale-[1.03] hover:border-sky-300 dark:border-sky-800/70 dark:bg-sky-950/30 dark:text-sky-300"
                          title={t('requests.copyOrderTitle')}
                        >
                          {r.order_id || '-'}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50/80 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-800/70 dark:bg-indigo-950/30 dark:text-indigo-300">
                          {fixedCounterpartyRate != null && fixedCounterpartyRate > 0
                            ? t('requests.counterpartyRateBadge', { rate: fixedRateNumber })
                            : t('requests.counterpartyRateBadge', { rate: '—' })}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${statusCss}`}>{label}</span>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (vndVal == null || vndVal <= 0) return;
                          void copyText(formatVndInt(vndVal));
                        }}
                        className="rounded-lg bg-gray-50/80 px-3 py-2 text-left transition hover:scale-[1.01] dark:bg-gray-800/70"
                        title={t('requests.copyVndTitle')}
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('requests.amountVnd')}</div>
                        <div className="font-semibold text-gray-900 underline decoration-dotted underline-offset-4 dark:text-gray-100">
                          {vndVal != null && vndVal > 0 ? formatVndInt(vndVal) : na}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (teamUsdt == null || teamUsdt <= 0) return;
                          void copyText(usdtNumber);
                        }}
                        className="rounded-lg bg-gray-50/80 px-3 py-2 text-left transition hover:scale-[1.01] dark:bg-gray-800/70"
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400">USDT</div>
                        <HoverTooltip text={usdtFullTooltip}>
                          <div className="font-semibold text-gray-900 underline decoration-dotted underline-offset-4 dark:text-gray-100">
                            {usdtNumber}
                          </div>
                        </HoverTooltip>
                      </button>
                    </div>

                    <div className="mt-3 rounded-lg bg-gray-50/80 px-3 py-2 dark:bg-gray-800/70">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('requests.issuedQr')}</div>
                          {qrId != null ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/qr-profiles?highlight=${qrId}`)}
                              className="font-medium text-gray-900 transition hover:text-emerald-700 hover:underline dark:text-gray-100 dark:hover:text-emerald-400"
                            >
                              {qrProfileView}
                            </button>
                          ) : (
                            <div className="font-medium text-gray-900 dark:text-gray-100 break-words">{qrProfileView}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500 dark:text-gray-400">{t('requests.created')}</div>
                          <div className="text-sm text-gray-800 dark:text-gray-100 mt-0.5">
                            <span className="tabular-nums">{formatCreatedDateTime(r.created_at, intlLocale, na)}</span>
                            {workMs != null && (
                              <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                {durationLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {canDecide && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                          disabled={actionBusyId === r.id || confirmBusyId === r.id || lockedRequestIds.has(r.id)}
                          onClick={() => openApproveConfirm(r)}
                          className="w-full rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:scale-[1.01] hover:from-emerald-600 hover:to-green-700 disabled:opacity-50"
                        >
                          {t('requests.approve')}
                        </button>
                        <button
                          disabled={actionBusyId === r.id || confirmBusyId === r.id || lockedRequestIds.has(r.id)}
                          onClick={() => openCancelConfirm(r)}
                          className="w-full rounded-xl bg-gradient-to-r from-red-500 to-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:scale-[1.01] hover:from-red-600 hover:to-rose-700 disabled:opacity-50"
                        >
                          {t('requests.cancelRequestBtn')}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <ConfirmApproveWidget
        isOpen={Boolean(approveConfirmRequest)}
        onClose={() => {
          if (approveConfirmBusy) return;
          setApproveConfirmRequest(null);
          setConfirmBusyId(null);
        }}
        onConfirm={() => {
          void onApproveConfirmed();
        }}
        wtRequestId={approveConfirmRequest?.id ?? null}
        airexOrderId={approveConfirmRequest?.order_id}
        amountVnd={approveConfirmRequest?.vnd_amount_calculated ?? null}
      />
      <ConfirmCancelWidget
        isOpen={Boolean(cancelConfirmRequest)}
        wtRequestId={cancelConfirmRequest?.id ?? null}
        airexOrderId={cancelConfirmRequest?.order_id}
        amountVnd={cancelConfirmRequest?.vnd_amount_calculated ?? null}
        onClose={() => {
          if (cancelConfirmBusy) return;
          setCancelConfirmRequest(null);
          setConfirmBusyId(null);
        }}
        onConfirm={async () => {
          await onCancelConfirmed();
        }}
      />
    </div>
  );
}
